import type { AdminChartRow } from '@/services/dashboard'

/**
 * Points for the dashboard's Growth card, from GET /admin/dashboard/chart rows.
 * Each row is one UTC day, so dates are formatted in UTC: formatting them in the
 * viewer's time zone labels every bar a day early for anyone west of UTC.
 */

/** Metrics counted per day; their period total is the sum of the days. */
export type FlowMetric = 'signups' | 'submissions' | 'leads' | 'appointments'
export type GrowthMetric = FlowMetric | 'mrr'
export type Granularity = 'day' | 'week'

export interface GrowthPoint {
    key: string
    /** Axis label: the day, or the Monday that starts the week. */
    label: string
    /** Tooltip and table heading. */
    title: string
    value: number
    /** Average of the last 7 complete days. Day view of a counted metric only, from the 7th day on. */
    average: number | null
    /** Today, or a week without all 7 of its days in the range yet. */
    incomplete: boolean
    note: string | null
}

// Each tab reads the field its card above counts: Completed submissions reads the
// completed count, not every submission.
const FIELD: Record<FlowMetric, Exclude<keyof AdminChartRow, 'date'>> = {
    signups: 'signups',
    submissions: 'completedSubmissions',
    leads: 'leads',
    appointments: 'appointments',
}

const DAY_MS = 86_400_000

const utcDay = (date: Date) => Math.floor(date.getTime() / DAY_MS)
const formatUtc = (date: Date, options: Intl.DateTimeFormatOptions) =>
    date.toLocaleDateString('en-US', { timeZone: 'UTC', ...options })
const shortDate = (date: Date) => formatUtc(date, { month: 'short', day: 'numeric' })
// Titles carry the year for dates outside the current one: a 12-month range
// starts and ends on the same month and day.
const yearIfOther = (date: Date, now: Date): Intl.DateTimeFormatOptions =>
    date.getUTCFullYear() === now.getUTCFullYear() ? {} : { year: 'numeric' }
const dayTitle = (day: Day, now: Date) =>
    day.isToday
        ? `Today, ${shortDate(day.date)}`
        : formatUtc(day.date, { weekday: 'short', month: 'short', day: 'numeric', ...yearIfOther(day.date, now) })
const weekTitle = (start: Date, now: Date) =>
    `Week of ${formatUtc(start, { month: 'short', day: 'numeric', ...yearIfOther(start, now) })}`
const valueOf = (row: AdminChartRow, metric: FlowMetric) => Number(row[FIELD[metric]] || 0)

interface Day {
    date: Date
    value: number
    isToday: boolean
}

function toDays(rows: AdminChartRow[], value: (row: AdminChartRow, index: number) => number, now: Date): Day[] {
    const today = utcDay(now)
    return rows.map((row, index) => {
        const date = new Date(row.date)
        return { date, value: value(row, index), isToday: utcDay(date) === today }
    })
}

/** Days grouped into weeks that start on Monday. */
function toWeekGroups(days: Day[]): { start: Date; days: Day[] }[] {
    const weeks: { start: Date; days: Day[] }[] = []
    for (const day of days) {
        const daysSinceMonday = (day.date.getUTCDay() + 6) % 7
        const start = new Date(day.date.getTime() - daysSinceMonday * DAY_MS)
        const last = weeks[weeks.length - 1]
        if (last && last.start.getTime() === start.getTime()) last.days.push(day)
        else weeks.push({ start, days: [day] })
    }
    return weeks
}

/** A tab total, bar value or table cell: whole units, $ for money. */
export const formatValue = (value: number, money?: boolean) => (money ? '$' : '') + Math.round(value).toLocaleString()

/** A 7-day average: one decimal for counts, whole dollars for money. */
export const formatAverage = (value: number, money?: boolean) =>
    money ? formatValue(value, true) : value.toLocaleString(undefined, { maximumFractionDigits: 1 })

/** A y-axis tick: thousands shortened to k. */
export const formatAxis = (value: number, money?: boolean) =>
    (money ? '$' : '') + (value >= 1000 ? `${Number((value / 1000).toFixed(1))}k` : `${value}`)

/** The metric's total over the whole range, today so far included. */
export function metricTotal(rows: AdminChartRow[], metric: FlowMetric): number {
    return rows.reduce((sum, row) => sum + valueOf(row, metric), 0)
}

/** One value per complete day, for a tab's small trend line. */
export function sparklineValues(rows: AdminChartRow[], metric: FlowMetric, now: Date = new Date()): number[] {
    return toDays(rows, (row) => valueOf(row, metric), now).filter((day) => !day.isToday).map((day) => day.value)
}

export function growthSeries(
    rows: AdminChartRow[],
    metric: FlowMetric,
    granularity: Granularity,
    now: Date = new Date()
): GrowthPoint[] {
    const days = toDays(rows, (row) => valueOf(row, metric), now)
    return granularity === 'week' ? toWeeks(days, now) : days.map((day, i) => toDayPoint(days, day, i, now))
}

/**
 * MRR by day, as the Revenue page's MRR trend draws it: each day is today's MRR of
 * the agencies that had signed up by then, so the last day equals the MRR card.
 * Built from the card's MRR and each later day's `revenue` (today's MRR by signup
 * day), which is the same number get_admin_mrr_trend gives for every day.
 */
export function mrrSeries(
    rows: AdminChartRow[],
    currentMrr: number,
    granularity: Granularity,
    now: Date = new Date()
): GrowthPoint[] {
    const levels: number[] = []
    let fromLaterSignups = 0
    for (let i = rows.length - 1; i >= 0; i--) {
        levels[i] = currentMrr - fromLaterSignups
        fromLaterSignups += Number(rows[i].revenue || 0)
    }
    const days = toDays(rows, (_row, index) => levels[index], now)
    if (granularity === 'week') {
        return toWeekGroups(days).map((week) => ({
            key: week.start.toISOString(),
            label: shortDate(week.start),
            title: weekTitle(week.start, now),
            value: week.days[week.days.length - 1].value,
            average: null,
            incomplete: false,
            note: null,
        }))
    }
    return days.map((day) => ({
        key: day.date.toISOString(),
        label: shortDate(day.date),
        title: dayTitle(day, now),
        value: day.value,
        average: null,
        incomplete: false,
        note: null,
    }))
}

function toDayPoint(days: Day[], day: Day, index: number, now: Date): GrowthPoint {
    const window = index >= 6 ? days.slice(index - 6, index + 1) : []
    const average = window.length === 7 && !window.some((d) => d.isToday)
        ? window.reduce((sum, d) => sum + d.value, 0) / 7
        : null
    return {
        key: day.date.toISOString(),
        label: shortDate(day.date),
        title: dayTitle(day, now),
        value: day.value,
        average,
        incomplete: day.isToday,
        note: day.isToday ? 'Today so far' : null,
    }
}

function toWeeks(days: Day[], now: Date): GrowthPoint[] {
    return toWeekGroups(days).map((week) => {
        const hasToday = week.days.some((day) => day.isToday)
        const dayCount = week.days.length
        return {
            key: week.start.toISOString(),
            label: shortDate(week.start),
            title: weekTitle(week.start, now),
            value: week.days.reduce((sum, day) => sum + day.value, 0),
            average: null,
            incomplete: hasToday || dayCount < 7,
            note: hasToday ? 'This week so far' : dayCount < 7 ? `${dayCount} of 7 days in this range` : null,
        }
    })
}
